/**
 * User-safe error formatting. Never expose stack traces, file paths, or code to the UI.
 */

import { z } from "zod";

export function isZodError(error: unknown): error is z.ZodError {
  return (
    error instanceof z.ZodError ||
    (typeof error === "object" &&
      error !== null &&
      "issues" in error &&
      Array.isArray((error as { issues: unknown }).issues))
  );
}

function sanitizeForUser(msg: string): string {
  if (!msg || typeof msg !== "string") return "Something went wrong. Please try again.";
  const lines = msg.split("\n").filter((line) => {
    const t = line.trim();
    return (
      t.length > 0 &&
      !/^\s*at\s+/.test(line) &&
      !/\(.*:\d+:\d+\)/.test(t) &&
      !/^[A-Z]:\\/.test(t) &&
      !/^\//.test(t)
    );
  });
  const out = lines[0]?.trim() || msg.trim();
  return out.length > 0 ? out : "Something went wrong. Please try again.";
}

function friendlyZodMessage(issue: z.ZodIssue): string {
  const msg = issue.message || "";
  if (issue.code === "invalid_type") {
    if (msg.includes("number") && msg.includes("string")) return "Must be a number.";
    if (msg.includes("string")) return "Must be text.";
  }
  if (issue.code === "too_small" && msg.length > 0) return msg;
  if (issue.code === "too_big" && msg.length > 0) return msg;
  return msg || "Invalid value.";
}

export function formatZodErrorForUser(error: z.ZodError): string {
  return error.issues
    .map((issue) => {
      const path = issue.path?.filter(Boolean).join(" ");
      const label = path ? path.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "Field";
      const reason = friendlyZodMessage(issue);
      return `${label}: ${reason}`;
    })
    .join(". ");
}

export function getSafeErrorMessage(error: unknown): string {
  if (isZodError(error)) return formatZodErrorForUser(error);
  if (error instanceof Error) return sanitizeForUser(error.message);
  if (typeof error === "string") return sanitizeForUser(error);
  return "Something went wrong. Please try again.";
}
